import {useCallback} from "react";
import {
    GuestCount,
    Reservation,
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    addMinutes,
    createReservationStartDateBasedOnPeriod
} from "crqlar-react-common/dist/utils/date.util";
import {
    useCurrentApp,
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useService} from "crqlar-react-common/dist/utils/hooks/services/useServices";

type Period = 'breakfast' | 'lunch' | 'dinner' | 'breakfast-plus-dinner' | 'blank';

export function useHalfboardReservationBuilder(restaurantId: string) {
    const appData = useCurrentApp();
    const sector = useSector(appData?.configuration?.sectorId);
    const defaultService = useService(sector?.defaultServiceId);

    const buildReservation = useCallback((params: {
        profileIds: string[];
        primaryProfileId?: string;
        targetTableId: string;
        checkInDate: Date;
        guestCounts?: GuestCount[];
        period: 'breakfast' | 'lunch' | 'dinner' | 'blank';
    }): Reservation => {
        const startDate = params.period === 'blank'
            ? new Date()
            : createReservationStartDateBasedOnPeriod(params.checkInDate, params.period);
        const durationMinutes = defaultService?.durationMinutes || 60;

        return {
            resourceIds: [restaurantId, params.targetTableId],
            profileIds: params.profileIds,
            // Mirrors the restaurant arrivals list, which stamps the stay's primary guest on the
            // draft so the dialog resolves the same lead guest for both creation paths.
            primaryProfileId: params.primaryProfileId,
            expectedStartDate: startDate,
            expectedEndDate: addMinutes(startDate, durationMinutes),
            status: ReservationStatus.CONFIRMED,
            sectorId: sector?.id,
            serviceId: defaultService?.id,
            guestCounts: params.guestCounts,
            meta: {
                eatPeriod: params.period,
            },
        } as Reservation;
    }, [restaurantId, sector?.id, defaultService]);

    const buildForPeriod = useCallback((params: {
        profileIds: string[];
        primaryProfileId?: string;
        targetTableId: string;
        checkInDate: Date;
        guestCounts?: GuestCount[];
        checkOutDate?: Date;
        period: Period;
    }): { draft: Reservation; linked: Reservation[] | undefined; checkoutDate: Date | undefined } => {
        let draft: Reservation;
        let linked: Reservation[] | undefined;

        const base = {
            profileIds: params.profileIds,
            primaryProfileId: params.primaryProfileId,
            targetTableId: params.targetTableId,
            checkInDate: params.checkInDate,
            guestCounts: params.guestCounts,
        };

        if (params.period === 'breakfast-plus-dinner') {
            const breakfastReservation = buildReservation({...base, period: 'breakfast'});
            const dinnerReservation = buildReservation({...base, period: 'dinner'});
            draft = breakfastReservation;
            linked = [breakfastReservation, dinnerReservation];
        } else {
            draft = buildReservation({...base, period: params.period as 'breakfast' | 'lunch' | 'dinner' | 'blank'});
            linked = undefined;
        }

        return {draft, linked, checkoutDate: params.checkOutDate};
    }, [buildReservation]);

    return {buildReservation, buildForPeriod};
}
